import React, { useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm, useFormState, useWatch } from 'react-hook-form'
import { useDispatch } from 'react-redux'

import { Box, Button, Flex, Heading } from '@chakra-ui/react'

import { FileInput, Input, Select } from '@/components/Form'

import useUser from '@/features/users/useUser'
import { hideLoadingOverlay, showLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'
import { camelToWords } from '@/utils/stringHelper'

//Constant
import MultiSelect from '@/components/Form/Select/MultiSelect'
import useFile from '@/features/files/useFile'
import { OfferDocumentTypes } from '@/features/offers/constants'
import { SystemVariableType } from '@/features/systemVariables/constants'
import useSystemVariable from '@/features/systemVariables/useSystemVariable'
import dirtyValues from '@/utils/dirtyValues'
import { toast } from 'react-toastify'
import useConfig from '../useConfig'

const OfferConfig = () => {
  const [maintenanceTermAndConditionFileId, setMaintenanceTermAndConditionFileId] = useState('')
  const [projectTermAndConditionFileId, setProjectTermAndConditionFileId] = useState('')
  const [offerCostSheetTemplateFileId, setOfferCostSheetTemplateFileId] = useState('')
  const [offerConditionOfPurchaseFileId, setOfferConditionOfPurchaseFileId] = useState('')

  const { useFetchAllConfigByCategory, useBatchUpdateConfigs, useUpdateConfig } = useConfig()

  const { useFetchFile } = useFile()
  const { useUserOptions } = useUser()
  const { useSystemVariableOptions } = useSystemVariable()

  const { data: allOfferConfigs } = useFetchAllConfigByCategory('offer')

  const { data: offerMaintenanceTermAndConditionFile } = useFetchFile(maintenanceTermAndConditionFileId)

  const { data: offerProjectTermAndConditionFile } = useFetchFile(projectTermAndConditionFileId)

  const { data: offerCostSheetTemplateFile } = useFetchFile(offerCostSheetTemplateFileId)
  const { data: offerConditionOfPurchaseFile } = useFetchFile(offerConditionOfPurchaseFileId)

  const dispatch = useDispatch()
  const batchUpdateConfigs = useBatchUpdateConfigs()
  const methods = useForm()
  const { dirtyFields } = useFormState({ control: methods.control })
  const maintenanceTermAndCondition = useWatch({ name: 'maintenanceTermAndCondition', control: methods.control })

  const documentOptions = useMemo(
    () => [
      {
        label: camelToWords('externalCommercialProposal'),
        value: OfferDocumentTypes.DOCUMENTS_EXTERNAL_COMMERCIAL_PROPOSAL,
      },
      {
        label: camelToWords('externalCommercialDeviation'),
        value: OfferDocumentTypes.DOCUMENTS_EXTERNAL_COMMERCIAL_DEVIATION,
      },
      {
        label: camelToWords('externalNDA'),
        value: OfferDocumentTypes.DOCUMENTS_EXTERNAL_NDA,
      },
      {
        label: camelToWords('externalCoverLetter'),
        value: OfferDocumentTypes.DOCUMENTS_EXTERNAL_COVER_LETTER,
      },
      {
        label: camelToWords('internalCostsheet'),
        value: OfferDocumentTypes.DOCUMENTS_INTERNAL_COSTSHEET,
      },
      {
        label: camelToWords('internalDamexE'),
        value: OfferDocumentTypes.DOCUMENTS_INTERNAL_DAMEXE,
      },
      {
        label: camelToWords('internalECCSupporting'),
        value: OfferDocumentTypes.DOCUMENTS_INTERNAL_ECC_SUPPORTING,
      },
      {
        label: camelToWords('internalPSS'),
        value: OfferDocumentTypes.DOCUMENTS_INTERNAL_PSS,
      },
      {
        label: camelToWords('internalOCC'),
        value: OfferDocumentTypes.DOCUMENTS_INTERNAL_OCC,
      },
      {
        label: camelToWords('internalLoA'),
        value: OfferDocumentTypes.DOCUMENTS_INTERNAL_LOA,
      },
      {
        label: camelToWords('internalContributorComments'),
        value: OfferDocumentTypes.DOCUMENTS_INTERNAL_CONTRIBUTOR_COMMENTS,
      },
      {
        label: camelToWords('internalInsuranceComments'),
        value: OfferDocumentTypes.DOCUMENTS_INTERNAL_INSURANCE_COMMENTS,
      },
      {
        label: camelToWords('internalBondComments'),
        value: OfferDocumentTypes.DOCUMENTS_INTERNAL_BOND_COMMENTS,
      },
      {
        label: camelToWords('internalLegalStatement'),
        value: OfferDocumentTypes.DOCUMENTS_INTERNAL_LEGAL_STATEMENT,
      },
      {
        label: camelToWords('internalLegalComments'),
        value: OfferDocumentTypes.DOCUMENTS_INTERNAL_LEGAL_STATEMENT,
      },
      {
        label: camelToWords('internalIPRChecklist'),
        value: OfferDocumentTypes.DOCUMENTS_INTERNAL_IPR_CHECKLIST,
      },
      {
        label: camelToWords('internalTRRReport'),
        value: OfferDocumentTypes.DOCUMENTS_INTERNAL_TRR_REPORT,
      },
      {
        label: camelToWords('tenderFile'),
        value: OfferDocumentTypes.DOCUMENTS_TENDER_FILE,
      },
    ],
    []
  )

  const { data: usersOptions } = useUserOptions({ valueKey: '_id', status: 'active' })
  const { data: teamOptions } = useSystemVariableOptions(SystemVariableType.TEAM, { status: 'active', valueKey: '_id' })

  useEffect(() => {
    if (Array.isArray(allOfferConfigs)) {
      allOfferConfigs.forEach((config) => {
        switch (config.type) {
          case 'SIGNATURE_LABEL':
            methods.setValue('signatureLabel', config.mainValue)
            break
          case 'SUBMITTED_OFFER_CHECKING':
            methods.setValue('mandatoryDocumentsForSubmission', config.value)
            break

          case 'OFFER_MAINTENANCE_TERM_AND_CONDTION_FILE':
            setMaintenanceTermAndConditionFileId(config?.mainValue)
            break

          case 'OFFER_PROJECT_TERM_AND_CONDTION_FILE':
            methods.setValue('', config.value)
            setProjectTermAndConditionFileId(config?.mainValue)
            break

          case 'OFFER_MAINTENANCE_VIEW_TEAM':
            methods.setValue('maintenanceViewTeam', config.value)
            break

          case 'OFFER_PROJECT_VIEW_TEAM':
            methods.setValue('projectViewTeam', config.value)
            break

          case 'OFFER_BOOKING_COMMERCIAL_TO_1':
            methods.setValue('bookingCommercial1', config.value)
            break

          case 'OFFER_BOOKING_COMMERCIAL_TO_2':
            methods.setValue('bookingCommercial2', config.value)
            break

          case 'OFFER_COST_SHEET_TEMPLATE':
            setOfferCostSheetTemplateFileId(config?.mainValue)
            break
          case 'OFFER_CONDITION_OF_PURCHASE_FILE':
            setOfferConditionOfPurchaseFileId(config?.mainValue)
            break
          case 'OFFER_SOURCING_THRESHOLD':
            methods.setValue('offerSourcingThreshold', config.mainValue)
            break
          case 'OFFER_PR_LEVEL_3_TECHNICAL_REQUIRED_THRESHOLD':
            methods.setValue('offerPRLevel3TechnicalRequiredThreshold', config.mainValue)
            break
          case 'OFFER_PR_SUBCONTRACTOR_AGREEMENT_REQUIRED_THRESHOLD':
            methods.setValue('offerPRSubcontractorAgreementRequiredThreshold', config.mainValue)
            break
          case 'BUILDING_X_TEAM_EMAIL':
            console.log('config.mainValue', config.mainValue)
            methods.setValue('buildingXTeamEmail', config.mainValue)
            break
          case 'OFFER_LOGISTIC_USER':
            methods.setValue('logisticUser', config.mainValue)
            break
          case 'OFFER_GCPR_LOGISTIC_USER':
            methods.setValue('gcprLogisticUser', config.mainValue)
            break
          case 'OFFER_HSP1_USER':
            methods.setValue('hsp1User', config.mainValue)
            break
        }
      })
    }
  }, [allOfferConfigs, methods])

  useEffect(() => {
    if (offerMaintenanceTermAndConditionFile?.data) {
      methods.setValue('maintenanceTermAndCondition', offerMaintenanceTermAndConditionFile?.data)
    }
  }, [methods, offerMaintenanceTermAndConditionFile?.data])
  useEffect(() => {
    if (offerProjectTermAndConditionFile?.data) {
      methods.setValue('projectTermAndCondition', offerProjectTermAndConditionFile?.data)
    }
  }, [methods, offerProjectTermAndConditionFile?.data])
  useEffect(() => {
    if (offerCostSheetTemplateFile?.data) {
      methods.setValue('offerCostSheetTemplate', offerCostSheetTemplateFile?.data)
    }
  }, [methods, offerCostSheetTemplateFile?.data])
  useEffect(() => {
    if (offerConditionOfPurchaseFile?.data) {
      methods.setValue('offerConditionOfPurchase', offerConditionOfPurchaseFile?.data)
    }
  }, [methods, offerConditionOfPurchaseFile?.data])

  const onSubmit = async (data) => {
    const dirtyData = dirtyValues(dirtyFields, data)
    // console.log('dirtyData', dirtyData)

    const updatedConfigs = []

    for (const [key, value] of Object.entries(dirtyData)) {
      // console.log(`${key}: ${value}`)

      switch (key) {
        case 'signatureLabel':
          updatedConfigs.push({
            type: 'SIGNATURE_LABEL',
            mainValue: value,
          })
          break
        case 'mandatoryDocumentsForSubmission':
          updatedConfigs.push({
            type: 'SUBMITTED_OFFER_CHECKING',
            value: value,
          })
          break
        case 'maintenanceTermAndCondition':
          updatedConfigs.push({
            type: 'OFFER_MAINTENANCE_TERM_AND_CONDTION_FILE',
            mainValue: value?._id,
          })
          break
        case 'projectTermAndCondition':
          updatedConfigs.push({
            type: 'OFFER_PROJECT_TERM_AND_CONDTION_FILE',
            mainValue: value?._id,
          })
          break
        case 'maintenanceViewTeam':
          updatedConfigs.push({
            type: 'OFFER_MAINTENANCE_VIEW_TEAM',
            value: value,
          })
          break
        case 'projectViewTeam':
          updatedConfigs.push({
            type: 'OFFER_PROJECT_VIEW_TEAM',
            value: value,
          })
          break
        case 'bookingCommercial1':
          updatedConfigs.push({
            type: 'OFFER_BOOKING_COMMERCIAL_TO_1',
            value: value,
          })
          break
        case 'bookingCommercial2':
          updatedConfigs.push({
            type: 'OFFER_BOOKING_COMMERCIAL_TO_2',
            value: value,
          })
          break
        case 'offerCostSheetTemplate':
          updatedConfigs.push({
            type: 'OFFER_COST_SHEET_TEMPLATE',
            mainValue: value?._id,
          })
          break
        case 'offerConditionOfPurchase':
          updatedConfigs.push({
            type: 'OFFER_CONDITION_OF_PURCHASE_FILE',
            mainValue: value?._id,
          })
          break
        case 'offerSourcingThreshold':
          updatedConfigs.push({
            type: 'OFFER_SOURCING_THRESHOLD',
            mainValue: `${value}`,
          })
          break
        case 'offerPRLevel3TechnicalRequiredThreshold':
          updatedConfigs.push({
            type: 'OFFER_PR_LEVEL_3_TECHNICAL_REQUIRED_THRESHOLD',
            mainValue: `${value}`,
          })
          break
        case 'offerPRSubcontractorAgreementRequiredThreshold':
          updatedConfigs.push({
            type: 'OFFER_PR_SUBCONTRACTOR_AGREEMENT_REQUIRED_THRESHOLD',
            mainValue: `${value}`,
          })
          break
        case 'buildingXTeamEmail':
          updatedConfigs.push({
            type: 'BUILDING_X_TEAM_EMAIL',
            mainValue: value,
          })
          break
        case 'logisticUser':
          updatedConfigs.push({
            type: 'OFFER_LOGISTIC_USER',
            mainValue: value,
          })
          break
        case 'gcprLogisticUser':
          updatedConfigs.push({
            type: 'OFFER_GCPR_LOGISTIC_USER',
            mainValue: value,
          })
          break
        case 'hsp1User':
          updatedConfigs.push({
            type: 'OFFER_HSP1_USER',
            mainValue: value,
          })
          break
      }
    }

    if (updatedConfigs.length === 0) {
      toast.info('No Config updated !')
      return
    }

    try {
      dispatch(showLoadingOverlay())

      await batchUpdateConfigs.mutateAsync({ data: updatedConfigs })
    } catch (err) {
      console.log(err)
    } finally {
      dispatch(hideLoadingOverlay())
    }
  }

  return (
    <Box shadow="md" borderWidth="1px" bg="white" w="100%" mb="auto">
      <Flex p={5} borderBottom="1px solid #d8dbe0">
        <Heading> Offer Config </Heading>
      </Flex>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <Box p={5}>
            <Flex direction={'column'} gap="4">
              <Input name="signatureLabel" label="Signature Label" isRow isRequired={false} />
              <MultiSelect
                name="mandatoryDocumentsForSubmission"
                label="Mandatory Documents for submission"
                options={documentOptions}
                isRow
                isRequired={false}
              />
              <MultiSelect
                name="bookingCommercial1"
                label="Booking Commercial Item Second Reminder Receiver"
                options={usersOptions}
                isRow
                isRequired={false}
              />
              <MultiSelect
                name="bookingCommercial2"
                label="Booking Commercial Item Third Reminder Receiver"
                options={usersOptions}
                isRow
                isRequired={false}
              />

              <FileInput
                label="Maintenance Terms and Condition File"
                name="maintenanceTermAndCondition"
                fileType="CONFIG_TERM_AND_CONDITION"
                canUpload
                isRequired={false}
                isRow
              />
              <FileInput
                label="Project Terms and Condition File"
                name="projectTermAndCondition"
                fileType="CONFIG_TERM_AND_CONDITION"
                canUpload
                isRequired={false}
                isRow
              />
              <FileInput
                label="Cost Sheet Template"
                name="offerCostSheetTemplate"
                fileType="CONFIG_FILE"
                canUpload
                isRequired={false}
                isRow
              />
              <FileInput
                label="Condition of Purchase"
                name="offerConditionOfPurchase"
                fileType="CONFIG_FILE"
                canUpload
                isRequired={false}
                isRow
              />
              <MultiSelect
                name="maintenanceViewTeam"
                label="Team that can view maintenance offer"
                options={teamOptions}
                isRow
                isRequired={false}
              />
              <MultiSelect
                name="projectViewTeam"
                label="Team that can view project offer"
                options={teamOptions}
                isRow
                isRequired={false}
              />
              <Input name="offerSourcingThreshold" label="Sourcing Threshold" isRow isRequired={false} type="number" />
              <Input
                name="offerPRLevel3TechnicalRequiredThreshold"
                label="PR - 3T Required Threshold"
                isRow
                isRequired={false}
                type="number"
              />
              <Input
                name="offerPRSubcontractorAgreementRequiredThreshold"
                label="PR - Sub-contractor Agreement Required Threshold"
                isRow
                isRequired={false}
                type="number"
              />
              <Input name="buildingXTeamEmail" label="Building X Team Email" isRow isRequired={false} />
              <Select name="logisticUser" label="Logistic User" options={usersOptions} isRow isRequired={false} />
              <Select
                name="gcprLogisticUser"
                label="GCPR Logistic User"
                options={usersOptions}
                isRow
                isRequired={false}
              />
              <Select name="hsp1User" label="HSP1 User" options={usersOptions} isRow isRequired={false} />
            </Flex>

            <Button mt="6" colorScheme="blue" type="submit">
              Save
            </Button>
          </Box>
        </form>
      </FormProvider>
    </Box>
  )
}

export default OfferConfig
